import './index.css';

type Status = 'shipped' | 'pending';

type LedgerItem = {
  stamp: string;
  title: string;
  dek: string;
  tags?: string;
  status: Status;
  href?: string;
};

const PROJECTS: LedgerItem[] = [
  {
    stamp: 'MACOS',
    title: 'DesktopVideoWallpaper',
    dek: 'A menu bar app that plays a video file as your animated desktop wallpaper — pick a clip, it loops quietly behind your icons.',
    tags: 'Swift · AVFoundation',
    status: 'shipped',
    href: 'https://github.com/mevans46/DesktopVideoWallpaper',
  },
  {
    stamp: 'WEB',
    title: 'TCG',
    dek: 'A trading card game, designed and shipped as its own standalone site.',
    tags: 'tcg.yonks.dev',
    status: 'shipped',
    href: 'https://tcg.yonks.dev',
  },
  {
    stamp: 'SITE',
    title: 'This site',
    dek: "What you're reading right now — rebuilt from scratch, no template.",
    tags: 'Bun · React · Tailwind',
    status: 'shipped',
  },
];

const WRITING: LedgerItem[] = [
  {
    stamp: 'TBD',
    title: 'First post',
    dek: 'Nothing published yet. Notes on distributed processing and fintech infrastructure are in the works.',
    status: 'pending',
  },
];

function LedgerRow({ item }: { item: LedgerItem }) {
  const inner = (
    <>
      <span className="ledger-stamp">{item.stamp}</span>
      <span className="ledger-body">
        <span className="ledger-title">{item.title}</span>
        <span className="ledger-dek">{item.dek}</span>
        {item.tags && <span className="ledger-tags">{item.tags}</span>}
      </span>
      {item.href && <span className="ledger-arrow">&#8599;</span>}
    </>
  );

  if (item.href) {
    return (
      <a
        className="ledger-row"
        href={item.href}
        target="_blank"
        rel="noreferrer"
      >
        {inner}
      </a>
    );
  }

  const rowClass =
    item.status === 'pending' ? 'ledger-row ledger-row--pending' : 'ledger-row';

  return <div className={rowClass}>{inner}</div>;
}

export default function App() {
  const year = new Date().getFullYear();

  return (
    <div className="page">
      <header className="site-header">
        <div className="wrap site-header-inner">
          <span className="wordmark">Matthew Evans</span>
          <nav className="nav">
            <a href="#projects" className="nav-label">Projects</a>
            <a href="#writing" className="nav-label">Writing</a>
            <a href="#about" className="nav-label">About</a>
            <a
              className="nav-cta"
              href="https://github.com/mevans46"
              target="_blank"
              rel="noreferrer"
            >
              GitHub
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="wrap">
            <p className="hero-eyebrow">Fintech Software Engineer</p>
            <h1>
              I'm a software engineer working in <em>fintech</em>.
            </h1>
            <p>
              Mostly backend and distributed systems — the infrastructure a
              financial product relies on to work correctly. I studied
              artificial intelligence at university, and these days spend
              most of my time here instead.
            </p>

            <div className="ledger-strip">
              <dl>
                <div>
                  <dt>Role</dt>
                  <dd>Fintech Software Engineer</dd>
                </div>
                <div>
                  <dt>Focus</dt>
                  <dd>Distributed processing systems</dd>
                </div>
                <div>
                  <dt>Education</dt>
                  <dd>BSc Artificial Intelligence</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>Active</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section id="projects" className="section">
          <div className="wrap">
            <div className="section-head">
              <span className="section-label">Projects</span>
              <span className="section-note">Shipped &amp; in progress</span>
            </div>
            <div className="ledger">
              {PROJECTS.map(item => (
                <LedgerRow key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section id="writing" className="section">
          <div className="wrap">
            <div className="section-head">
              <span className="section-label">Writing</span>
              <span className="section-note">Field notes</span>
            </div>
            <p className="section-intro">
              Occasional notes on distributed systems, fintech
              infrastructure, and whatever I'm building. Nothing published
              yet — reach out at{' '}
              <a
                href="mailto:matt@yonks.dev"
                style={{ color: 'var(--accent)' }}
              >
                matt@yonks.dev
              </a>{' '}
              to get pinged when the first one lands.
            </p>
            <div className="ledger">
              {WRITING.map(item => (
                <LedgerRow key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section id="about" className="section">
          <div className="wrap">
            <div className="section-head">
              <span className="section-label">About</span>
            </div>
            <div className="about-grid">
              <div className="about-copy">
                <p>
                  I'm a software engineer working in fintech, mostly on
                  distributed processing systems — the backend layer that
                  other parts of the product depend on.
                </p>
                <p>
                  I studied artificial intelligence at university. These
                  days I mostly work on distributed systems that move and
                  reconcile money correctly.
                </p>
              </div>
              <div className="capabilities">
                <dl>
                  <div>
                    <dt>Languages</dt>
                    <dd>JavaScript · Java · Python · C++ · CSS</dd>
                  </div>
                  <div>
                    <dt>Frameworks</dt>
                    <dd>React · Spring Boot</dd>
                  </div>
                  <div>
                    <dt>Distributed Systems</dt>
                    <dd>Apache Kafka</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="wrap footer-inner">
          <span>
            © {year} Matthew Evans — built with Bun + React, no trackers.
          </span>
          <span className="footer-links">
            <a href="https://github.com/mevans46" target="_blank" rel="noreferrer">
              GitHub
            </a>
            <a
              href="https://linkedin.com/in/matt-evans-675463194"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>
            <a href="mailto:matt@yonks.dev">Email</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
